import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type PageBinding } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { Icon } from './Icons.js';

/**
 * Which agent answers for which Page.
 *
 * One agent per Page, and the database enforces it — `pageId` is unique on the
 * binding table. That is the design rather than a limit: a Page about one
 * product has one body of knowledge, and an agent asked to answer for every
 * Page at once answers each of them worse than a specialist would.
 *
 * The binding lived only in the API until now, which meant an operator could
 * create agents in this console and had nowhere to point them.
 */
export function PageBindings() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const bindings = useQuery({
    queryKey: ['automation-bindings'],
    queryFn: api.automationBindings,
  });
  const agents = useQuery({ queryKey: ['agents'], queryFn: api.agents });

  const refresh = (text: string) => {
    setNotice({ kind: 'ok', text });
    void queryClient.invalidateQueries({ queryKey: ['automation-bindings'] });
  };

  const fail = (error: unknown) =>
    setNotice({
      kind: 'error',
      text: error instanceof ApiError ? error.message : t('common.requestFailed'),
    });

  const save = useMutation({
    mutationFn: ({
      pageId,
      input,
    }: {
      pageId: string;
      input: Parameters<typeof api.bindAgent>[1];
    }) => api.bindAgent(pageId, input),
    onSuccess: () => refresh(t('bindings.saved')),
    onError: fail,
  });

  const unbind = useMutation({
    mutationFn: (pageId: string) => api.unbindAgent(pageId),
    onSuccess: () => refresh(t('bindings.removed')),
    onError: fail,
  });

  if (bindings.isLoading || agents.isLoading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const available = (agents.data?.agents ?? []).filter((agent) => agent.isActive);
  const pages = bindings.data?.pages ?? [];

  if (available.length === 0) {
    return (
      <div className="empty-state">
        <Icon.robot />
        {/* Pointing at the missing prerequisite rather than showing an empty
            list of Pages nobody can bind anything to. */}
        <p>{t('bindings.noAgents')}</p>
      </div>
    );
  }

  /** Which agent a row is proposing — the staged choice, or what is stored. */
  const chosen = (page: PageBinding): string =>
    drafts[page.pageId] ?? page.binding?.agentConfigId ?? '';

  return (
    <>
      <p className="muted small">{t('bindings.hint')}</p>
      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {pages.length === 0 && <p className="muted">{t('bindings.noPages')}</p>}

      {pages.map((page) => {
        const current = page.binding;
        const selected = chosen(page);
        const dirty = selected !== (current?.agentConfigId ?? '');

        return (
          <article key={page.pageId} className="card">
            <header className="card-head">
              <h3>{page.name}</h3>
              <div>
                {current ? (
                  <span className="badge ok">{t('bindings.bound')}</span>
                ) : (
                  <span className="badge">{t('bindings.unbound')}</span>
                )}
                {/* A Page can be bound and still never be asked anything: Meta
                    only delivers comments to Pages subscribed to the webhook. */}
                {current?.autoReplyComments && !page.webhookSubscribed && (
                  <span className="badge warn">{t('bindings.notSubscribed')}</span>
                )}
              </div>
            </header>

            {!page.canComment ? (
              // Shown rather than hidden: an operator looking for a Page that
              // silently vanished has no way to learn why.
              <p className="muted small">{t('bindings.cannotComment')}</p>
            ) : (
              <>
                <label htmlFor={`agent-${page.pageId}`}>{t('bindings.agent')}</label>
                <select
                  id={`agent-${page.pageId}`}
                  value={selected}
                  onChange={(event) => setDrafts({ ...drafts, [page.pageId]: event.target.value })}
                >
                  <option value="">{t('bindings.chooseAgent')}</option>
                  {available.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>

                {current && (
                  <div className="binding-switches">
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={current.autoReplyComments}
                        onChange={(event) =>
                          save.mutate({
                            pageId: page.pageId,
                            input: {
                              agentConfigId: current.agentConfigId,
                              autoReplyComments: event.target.checked,
                              autoPublishReplies: current.autoPublishReplies,
                              autoDraftInbox: current.autoDraftInbox,
                              minCommentLength: current.minCommentLength,
                              maxRepliesPerHour: current.maxRepliesPerHour,
                            },
                          })
                        }
                      />
                      {t('bindings.autoReply')}
                    </label>

                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={current.autoPublishReplies}
                        disabled={!current.autoReplyComments}
                        onChange={(event) =>
                          save.mutate({
                            pageId: page.pageId,
                            input: {
                              agentConfigId: current.agentConfigId,
                              autoReplyComments: current.autoReplyComments,
                              autoPublishReplies: event.target.checked,
                              autoDraftInbox: current.autoDraftInbox,
                              minCommentLength: current.minCommentLength,
                              maxRepliesPerHour: current.maxRepliesPerHour,
                            },
                          })
                        }
                      />
                      {t('bindings.autoPublish')}
                    </label>
                    {/* The one setting that lets an agent speak to the public
                        unattended. It says so, rather than sitting quietly
                        beside the others. */}
                    <p className="tiny muted">{t('bindings.autoPublishHint')}</p>

                    {/* Not tied to the two above. An account that will never let
                        an agent speak to a customer unattended may still want
                        its help writing, and requiring the first switch would
                        deny them that. */}
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={current.autoDraftInbox}
                        onChange={(event) =>
                          save.mutate({
                            pageId: page.pageId,
                            input: {
                              agentConfigId: current.agentConfigId,
                              autoReplyComments: current.autoReplyComments,
                              autoPublishReplies: current.autoPublishReplies,
                              autoDraftInbox: event.target.checked,
                              minCommentLength: current.minCommentLength,
                              maxRepliesPerHour: current.maxRepliesPerHour,
                            },
                          })
                        }
                      />
                      {t('bindings.autoDraft')}
                    </label>
                    <p className="tiny muted">{t('bindings.autoDraftHint')}</p>
                  </div>
                )}

                <div className="actions">
                  {dirty && selected && (
                    <button
                      type="button"
                      className="primary small"
                      disabled={save.isPending}
                      onClick={() =>
                        save.mutate({
                          pageId: page.pageId,
                          input: {
                            agentConfigId: selected,
                            // A newly bound Page starts off: nothing answers the
                            // public until somebody switches it on deliberately.
                            autoReplyComments: current?.autoReplyComments ?? false,
                            autoPublishReplies: current?.autoPublishReplies ?? false,
                            autoDraftInbox: current?.autoDraftInbox ?? false,
                            minCommentLength: current?.minCommentLength ?? 2,
                            maxRepliesPerHour: current?.maxRepliesPerHour ?? 20,
                          },
                        })
                      }
                    >
                      <Icon.check />
                      {current ? t('bindings.rebind') : t('bindings.bind')}
                    </button>
                  )}

                  {current && (
                    <button
                      type="button"
                      className="ghost small"
                      disabled={unbind.isPending}
                      onClick={() => unbind.mutate(page.pageId)}
                    >
                      {t('bindings.unbind')}
                    </button>
                  )}
                </div>
              </>
            )}
          </article>
        );
      })}
    </>
  );
}
